import { defaultAppChannel } from "@/utils/channel";

export interface DateDuration {
  seconds?: number;
  minutes?: number;
  hours?: number;
  days?: number;
  weeks?: number;
  months?: number;
  years?: number;
}

export const TIME_UNITS = {
  seconds: 1000,
  minutes: 60 * 1000,
  hours: 60 * 60 * 1000,
  days: 24 * 60 * 60 * 1000,
  weeks: 7 * 24 * 60 * 60 * 1000,
  months: 30 * 24 * 60 * 60 * 1000,
  years: 365 * 24 * 60 * 60 * 1000,
} as const;

export function durationToMilliseconds(duration: DateDuration): number {
  let time = 0;

  if (duration.seconds) {
    time += duration.seconds * TIME_UNITS.seconds;
  }
  if (duration.minutes) {
    time += duration.minutes * TIME_UNITS.minutes;
  }
  if (duration.hours) {
    time += duration.hours * TIME_UNITS.hours;
  }
  if (duration.days) {
    time += duration.days * TIME_UNITS.days;
  }
  if (duration.weeks) {
    time += duration.weeks * TIME_UNITS.weeks;
  }
  if (duration.months) {
    time += duration.months * TIME_UNITS.months;
  }
  if (duration.years) {
    time += duration.years * TIME_UNITS.years;
  }

  return time;
}

type RelativeTimeFormatStyle = "long" | "short" | "narrow";

export function formatDateForPicker(date: Date) {
  const year = date.getFullYear();
  const month = padNumber(date.getMonth() + 1);
  const day = date.getDate();
  const hours = padNumber(date.getHours());
  const minutes = padNumber(date.getMinutes());

  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

export function parseDateFromPicker(date: string) {
  const [dateString, timeString] = date.split("T");

  if (!dateString || !timeString) {
    throw new Error("Invalid date format");
  }

  const [year, month, day] = dateString.split("-");
  const [hours, minutes] = timeString.split(":");

  if (!year || !month || !day || !hours || !minutes) {
    throw new Error("Invalid date format");
  }

  return new Date(
    parseInt(year, 10),
    parseInt(month, 10) - 1,
    parseInt(day, 10),
    parseInt(hours, 10),
    parseInt(minutes, 10),
  );
}

export function padNumber(num: number, length = 2) {
  return num.toString().padStart(length, "0");
}

// Local replacements for common date-fns helpers to avoid bundling issues

export function parseISO(isoString: string): Date {
  return new Date(isoString);
}

export function formatISO(
  date: Date,
  options?: { representation?: "complete" | "date" | "time" },
): string {
  const repr = options?.representation ?? "complete";
  if (repr === "date") {
    const y = date.getUTCFullYear();
    const m = padNumber(date.getUTCMonth() + 1);
    const d = padNumber(date.getUTCDate());
    return `${y}-${m}-${d}`;
  }
  if (repr === "time") {
    const hh = padNumber(date.getUTCHours());
    const mm = padNumber(date.getUTCMinutes());
    const ss = padNumber(date.getUTCSeconds());
    return `${hh}:${mm}:${ss}Z`;
  }
  // complete: ISO string without milliseconds
  const iso = date.toISOString();
  return iso.includes(".") ? `${iso.split(".")[0]}Z` : iso;
}

export function format(date: Date, pattern: string): string {
  // Support the patterns used in the app; extend as needed
  if (pattern === "yyyy-MM-dd'T'HH:mm") {
    // local time (used in inputs)
    return formatDateForPicker(date);
  }
  if (pattern === "MMM d, yyyy") {
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    }).format(date);
  }
  // Fallback
  return formatISO(date, { representation: "complete" });
}

export function addDays(date: Date, amount: number): Date {
  return new Date(date.getTime() + amount * 24 * 60 * 60 * 1000);
}

export function subDays(date: Date, amount: number): Date {
  return addDays(date, -amount);
}

export function addWeeks(date: Date, amount: number): Date {
  return addDays(date, amount * 7);
}

export function addMonths(date: Date, amount: number): Date {
  const d = new Date(date.getTime());
  d.setMonth(d.getMonth() + amount);
  return d;
}

export function subMonths(date: Date, amount: number): Date {
  return addMonths(date, -amount);
}

export function isAfter(a: Date, b: Date): boolean {
  return a.getTime() > b.getTime();
}

export function isBefore(a: Date, b: Date): boolean {
  return a.getTime() < b.getTime();
}

export function endOfMonth(date: Date): Date {
  const y = date.getFullYear();
  const m = date.getMonth();
  const startNext = new Date(y, m + 1, 1, 0, 0, 0, 0);
  return new Date(startNext.getTime() - 1);
}

export function startOfWeek(date: Date, weekStartsOn = 0): Date {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const day = d.getDay();
  const diff = (day - weekStartsOn + 7) % 7;
  d.setDate(d.getDate() - diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

export function endOfWeek(date: Date, weekStartsOn = 0): Date {
  const start = startOfWeek(date, weekStartsOn);
  const end = new Date(start.getTime());
  end.setDate(end.getDate() + 7);
  end.setMilliseconds(end.getMilliseconds() - 1);
  return end;
}

export function differenceInSeconds(a: Date, b: Date): number {
  return Math.max(0, Math.floor((a.getTime() - b.getTime()) / 1000));
}

export function differenceInMinutes(a: Date, b: Date): number {
  return Math.max(0, Math.floor((a.getTime() - b.getTime()) / (60 * 1000)));
}

export function differenceInHours(a: Date, b: Date): number {
  return Math.max(
    0,
    Math.floor((a.getTime() - b.getTime()) / (60 * 60 * 1000)),
  );
}

export function differenceInDays(a: Date, b: Date): number {
  return Math.max(
    0,
    Math.floor((a.getTime() - b.getTime()) / (24 * 60 * 60 * 1000)),
  );
}

// time difference in seconds
function timeDiffFromNow(date: Date): number {
  const diff = Math.floor((new Date().getTime() - date.getTime()) / 1000);
  return diff;
}

export function timeAgoWithStyle(
  date: Date,
  style: RelativeTimeFormatStyle,
  locale: string,
): string {
  // multiple time diff with -1 to get time ago
  // see: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/RelativeTimeFormat
  const diff = timeDiffFromNow(date);
  const diffAbs = Math.abs(diff);
  const formatter = new Intl.RelativeTimeFormat(locale, { style });

  if (diffAbs < 60) {
    return formatter.format(-diff, "seconds");
  } else if (diffAbs < 3600) {
    return formatter.format(Math.floor(-(diff / 60)), "minutes");
  } else if (diffAbs < 3600 * 24) {
    return formatter.format(Math.floor(-(diff / 3600)), "hours");
  }

  return formatter.format(Math.floor(-(diff / (3600 * 24))), "days");
}

export function timeAgoNarrow(date: string | Date, locale: string): string {
  return timeAgoWithStyle(new Date(date), "narrow", locale);
}

export function timeAgo(date: Date, locale: string): string {
  return timeAgoWithStyle(date, "long", locale);
}

export function defaultShowStartTime(): Date {
  return new Date(new Date().getTime() + 10 * 60 * 1000);
}

export function yesterday(): Date {
  const date = new Date();
  date.setDate(date.getDate() - 1);
  return date;
}

export const reverseToLocalDateTimeString = (
  isoString: string,
): string | undefined => {
  const date = parseISO(isoString);

  // Check if the date is valid
  if (isNaN(date.getTime())) {
    // throw new Error(`Invalid ISO date-time string: ${isoString}`);
    return undefined;
  }

  // Format the date back to `YYYY-MM-DDTHH:MM`
  const localDateTimeString = format(date, "yyyy-MM-dd'T'HH:mm");

  return localDateTimeString;
};

export const ensureValidDateTimeString = (
  dateTimeString: string | null,
): string | undefined => {
  if (!dateTimeString) {
    return undefined;
  }

  const date = new Date(dateTimeString);

  // Check if the date is valid
  if (isNaN(date.getTime())) {
    // throw new Error(`Invalid date-time string: ${dateTimeString}`);
    return undefined;
  }

  // Format the date to ISO 8601 string without milliseconds
  const isoString = formatISO(date, { representation: "complete" });

  return isoString;
};

export const getValidContestDateTimeString = (
  dateTimeString: string,
): string | undefined | null => {
  if (!dateTimeString) {
    return null;
  }

  const date = new Date(dateTimeString);

  // Check if the date is valid
  if (isNaN(date.getTime())) {
    // throw new Error(`Invalid date-time string: ${dateTimeString}`);
    return undefined;
  }

  // Format the date to ISO 8601 string without milliseconds
  const isoString = formatISO(date, { representation: "complete" });

  return isoString;
};

export const getStartOfMonthWithOffset = (channelSlug?: string) => {
  const channelPointsExtendedDays = channelSlug === defaultAppChannel ? 1 : 0;
  const currentDate = new Date();

  // If we’re on or before day (1 + extendedDays), shift to previous month
  if (currentDate.getUTCDate() <= 1 + channelPointsExtendedDays) {
    return startOfUTCMonth(subMonths(currentDate, 1));
  }

  // Otherwise, use the current month
  return startOfUTCMonth(currentDate);
};

export function startOfUTCMonth(date: Date) {
  const year = date.getUTCFullYear();
  const month = date.getUTCMonth();
  return new Date(Date.UTC(year, month, 1, 0, 0, 0));
}

export function endOfUTCMonth(date: Date) {
  // The start of the *next* month in UTC, minus 1ms
  const year = date.getUTCFullYear();
  const month = date.getUTCMonth();
  const startNextMonth = new Date(Date.UTC(year, month + 1, 1, 0, 0, 0));
  return new Date(startNextMonth.getTime() - 1);
}

export function startOfUTCWeek(date: Date, weekStartsOn = 1) {
  const year = date.getUTCFullYear();
  const month = date.getUTCMonth();
  const day = date.getUTCDate();

  const utcMidnight = new Date(Date.UTC(year, month, day, 0, 0, 0));

  const currentDayOfWeek = utcMidnight.getUTCDay();
  const diff = (currentDayOfWeek - weekStartsOn + 7) % 7;

  utcMidnight.setUTCDate(utcMidnight.getUTCDate() - diff);
  return utcMidnight;
}

export function endOfUTCWeek(date: Date, weekStartsOn = 1) {
  const start = startOfUTCWeek(date, weekStartsOn);
  // Move forward by 7 days
  start.setUTCDate(start.getUTCDate() + 7);
  // Subtract 1 ms -> Sunday 23:59:59.999 UTC
  return new Date(start.getTime() - 1);
}

export function formatHumanReadableDate(
  date: Date,
  locale: string, // defaults to browser language
): string {
  // Build (and cache) formatters per locale
  const fmt = {
    date: new Intl.DateTimeFormat(locale, {
      weekday: "long",
      day: "numeric",
      month: "long",
    }),
    time: new Intl.DateTimeFormat(locale, {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false, // 24h
    }),
  };

  // Left side (weekday + day + month) with the locale’s own literals (“de”, commas, spaces…)
  const left = fmt.date
    .formatToParts(date)
    .filter((p) => p.type !== "era" && p.type !== "year")
    .map((p) => p.value)
    .join("");

  // Right side: force 24h HH:mm
  const t = fmt.time.formatToParts(date);
  const hh = t.find((p) => p.type === "hour")?.value ?? "";
  const mm = t.find((p) => p.type === "minute")?.value ?? "";

  return `${left} | ${hh}:${mm}`;
}
